<template>
	<div class="basic-info">
		<el-form :model="form" ref="form" label-width="auto" size="mini" :rules="addRules"
			:disabled="formDisabled || mode === 'view'">
			<div class="form-scroll-container">
				<el-row :gutter="8" class="dynamic-fields-grid">
					<!-- 动态渲染所有字段 -->
					<template v-for="field in dynamicFields">
						<el-col v-if="field.isShow && !isPhamTypeField(field)" :key="field.fieldCode" :span="6" class="field-col">
							<el-form-item :label="field.fieldName + '：'" :prop="field.fieldCode"
								:rules="getFieldRules(field)" class="form-item-compact form-item-adaptive">
								<!-- 输入框 -->
								<el-input v-if="field.inputBoxType === 'input'" v-model="form[field.fieldCode]"
									:placeholder="getFieldPlaceholder(field)" @blur="updateForm" clearable
									class="compact-input"></el-input>

								<!-- 下拉选择框 -->
								<el-select v-else-if="field.inputBoxType === 'selected'" v-model="form[field.fieldCode]"
									:placeholder="getFieldPlaceholder(field)" @change="handleFieldChange(field)" filterable
									clearable class="compact-select">
									<el-option v-for="option in getFieldOptions(field)" :key="getOptionKey(option)"
										:label="option.label" :value="option.id"></el-option>
								</el-select>

								<!-- 复选框 -->
								<template v-else-if="field.inputBoxType === 'checked'">
									<el-checkbox v-model="form[field.fieldCode]" @change="handleFieldChange(field)"
									:true-label="1" :false-label="0"
										class="compact-checkbox">
										{{ field.fieldName }}
									</el-checkbox>
								</template>

								<!-- 文本域 -->
								<el-input v-else-if="field.inputBoxType === 'textarea'" type="textarea" :rows="2"
									v-model="form[field.fieldCode]" @blur="updateForm"
									:placeholder="getFieldPlaceholder(field)" class="compact-textarea"></el-input>

								<!-- 级联选择器（非药理类别） -->
								<el-cascader v-else-if="field.inputBoxType === 'cascader' && !isPhamTypeField(field)"
									v-model="form[field.fieldCode]" :options="getCascaderOptions(field)"
									:props="getCascaderProps(field)" :placeholder="getFieldPlaceholder(field)"
									@change="handleFieldChange(field)" clearable filterable
									class="compact-cascader"></el-cascader>

								<!-- 默认输入框 -->
								<el-input v-else v-model="form[field.fieldCode]" :placeholder="getFieldPlaceholder(field)"
									@blur="updateForm" clearable class="compact-input"></el-input>
							</el-form-item>
						</el-col>
					</template>

					<!-- 药理类别特殊处理：合并为一个级联选择器 -->
					<el-col v-if="hasPhamTypeFields" :key="'phamType-cascader'" :span="12" class="field-col">
						<el-form-item label="药理类别：" prop="phamType" :rules="getPhamTypeRules()" class="form-item-compact form-item-adaptive">
							<el-cascader v-model="phamTypeValue" :options="phamTypeOptions" :props="cascaderProps"
								:show-all-levels="true" clearable filterable placeholder="请选择药理类别"
								@change="handlePhamTypeChange" @visible-change="handleCascaderVisible"
								class="compact-cascader pham-type-cascader" popper-class="pham-type-cascader">
								<template slot-scope="{ node, data }">
									<span>{{ data.label }}</span>
									<span v-if="data.loading" class="loading-icon">
										<i class="el-icon-loading"></i>
									</span>
								</template>
							</el-cascader>
						</el-form-item>
					</el-col>

					<!-- 隐藏的原始表单项（用于数据绑定） -->
					<el-col v-if="hasPhamTypeFields" :span="6" style="display: none;">
						<el-form-item label="药理类别I：" prop="phamType1" class="form-item-compact form-item-adaptive">
							<el-input v-model="form.phamType1" class="compact-input"></el-input>
						</el-form-item>
					</el-col>

					<el-col v-if="hasPhamTypeFields" :span="6" style="display: none;">
						<el-form-item label="药理类别II：" prop="phamType2" class="form-item-compact form-item-adaptive">
							<el-input v-model="form.phamType2" class="compact-input"></el-input>
						</el-form-item>
					</el-col>

					<el-col v-if="hasPhamTypeFields" :span="6" style="display: none;">
						<el-form-item label="药理类别III：" prop="phamType3" class="form-item-compact form-item-adaptive">
							<el-input v-model="form.phamType3" class="compact-input"></el-input>
						</el-form-item>
					</el-col>
				</el-row>
			</div>
		</el-form>
	</div>
</template>

<script>
	// 导入API
	import { dict } from "@/api/staff/staff.js";
	import {
		getDrugUnt,
		getDosaUnt,
		getMedicationFrequency,
		getMedicationWay,
		getMedcas
	} from "@/api/MedicenCata.js";
	import {
		selectDocVtByType,
		selectOneCateGory,
		selectTwoCateGory,
		selectThreeCateGory
	} from "@/api/MedicenCata.js";

	export default {
		props: {
			form: {
				type: Object,
				required: true
			},
			formDisabled: {
				type: Boolean,
				default: false
			},
			mode: {
				type: String,
				default: 'add'
			}
		},
		data() {
			return {
				// 动态字段相关数据
				dynamicFields: [],
				fieldOptionsMap: new Map(),
				fieldRulesMap: new Map(),
				fieldCodeOptionsMap: new Map(),

				// 药理类别相关数据
				phamTypeValue: [],
				phamTypeOptions: [],
				cascaderProps: {
					value: 'id',
					label: 'label',
					children: 'children',
					lazy: true,
					lazyLoad: this.loadPhamTypeChildren,
					checkStrictly: false,
					emitPath: true
				},
				loadingPhamTypes: false,
				phamTypeCache: new Map(),

				// 级联选择器相关数据
				cascaderOptionsMap: new Map(),
				cascaderLoadingMap: new Map(),
				rxTypeoptions: [],
				// 选项数据映射表
				optionDataMap: {
					YNoptions: [{
							id: 0,
							label: '否'
						},
						{
							id: 1,
							label: '是'
						}
					],
					statusOptions: [{
							id: 0,
							label: '禁用'
						},
						{
							id: 1,
							label: '可用'
						}
					],
					qltTypeOptions:[
						{ id: 1, label: ' 一类' },{ id: 2, label: ' 二类' },{ id: 3, label: ' 三类' },
					],
					highRiskOption: [{
							id: 0,
							label: '非高危药品'
						},
						{
							id: 1,
							label: 'A级高危'
						},
						{
							id: 2,
							label: 'B级高危'
						},
						{
							id: 3,
							label: 'C级高危'
						},
					],
					roundUntoptions: [{
						id: 1,
						label: '单次取整'
					}, {
						id: 2,
						label: '总量取整'
					}, {
						id: 3,
						label: '处方取整'
					}],
					saveOptions: [{
							id: 1,
							label: '常温(0~30°)'
						},
						{
							id: 2,
							label: '阴凉(2~20°)'
						},
						{
							id: 3,
							label: '冷藏(2~8°)'
						},
						{
							id: 4,
							label: '凉暗(2~20°且避光)'
						}
					],
					antcB: [{
							id: 1,
							label: '一级抗生素'
						},
						{
							id: 2,
							label: '二级抗生素'
						},
						{
							id: 3,
							label: '三级抗生素'
						}
					],
					chargedSpcOptions: [{
							id: 0,
							label: '全部'
						},
						{
							id: 1,
							label: '门诊'
						},
						{
							id: 2,
							label: '其他'
						},
						{
							id: 3,
							label: '不计费'
						}
					],
					UseOptions: [{
							id: 0,
							label: '全院',
							value: '全院'
						},
						{
							id: 1,
							label: '门诊',
							value: '门诊'
						},
						{
							id: 2,
							label: '其他',
							value: '其他'
						}
					],
					nurtureB: [{
							id: 0,
							label: '否'
						},
						{
							id: 1,
							label: '普通'
						},
						{
							id: 2,
							label: '重点'
						},
						{
							id: 3,
							label: '特殊'
						}
					],
					nurtureType: [{
							id: 1,
							label: '常规养护',
							value: '常规养护'
						},
						{
							id: 2,
							label: '重点养护',
							value: '重点养护'
						}
					],
					expensiveTypeOptions: [
					  { id: 1, label: '普价' },
					  { id: 2, label: '贵重' },
					  { id: 3, label: '昂贵' },
					  { id: 4, label: '免费' }
					],
					liquidOptions: [{
							id: 0,
							label: '非液体'
						},
						{
							id: 1,
							label: '大液体'
						},
						{
							id: 2,
							label: '小液体'
						}
					],
				},

				// API加载的选项数据
				minUnt: [],
				dosUnt: [],
				allUnitOptions: [],
				usedFrquIdOptions: [], // usedFrquIdOptions
				medcWayIdOptions: [],
				medicalRecordFeeOption: [],
				poisonousTypeOption: [],

				addRules: {},
				formDisabledAntcLv: true,

				// 字段配置处理状态
				fieldConfigProcessed: false,
				pendingFieldConfig: null,
			};
		},
		computed: {
			hasPhamTypeFields() {
				return this.dynamicFields.some(field => this.isPhamTypeField(field));
			},

			phamTypeFields() {
				return this.dynamicFields.filter(field => this.isPhamTypeField(field));
			}
		},
		watch: {
			dynamicFields: {
				deep: true,
				handler() {
					this.$nextTick(() => {
						this.rebuildFieldMaps();
						if (this.hasPhamTypeFields) {
							this.initPhamTypeCascader();
						}
					});
				}
			},

			'form': {
				deep: true,
				immediate: true,
				handler(newForm) {
					if (this.mode === 'edit' && this.hasPhamTypeFields &&
						(newForm.phamType1 || newForm.phamType2 || newForm.phamType3)) {
						this.$nextTick(() => {
							this.initPhamTypeValue();
						});
					}
				}
			}
		},
		mounted() {
			this.$nextTick(async () => {
				try {
					await Promise.all([
						this.loadUnitOptions(),
						this.loadMedicationOptions(),
						this.loadOtherOptions()
					]);

					// //console.log('F3组件所有选项数据加载完成');
					this.getditc();
					if (this.pendingFieldConfig) {
						//console.log('处理等待中的字段配置');
						this.processPendingFieldConfig();
					} else {
						// //console.log('没有等待的字段配置，使用默认字段');
						this.setupDefaultFields();
					}

					if (this.hasPhamTypeFields) {
						this.initPhamTypeCascader();
					}
				} catch (error) {
					//console.error('F3组件初始化失败:', error);
					this.$message.error('F3组件初始化失败');
				}
			});
		},
		methods: {
			// 字典数据
			async getditc() {
			  this.result = await dict();
			  // 处理处方类型选项，添加颜色样式
			  this.rxTypeoptions = this.result.data.rxTypeVOS.map(v => {
			    return { 
			      label: v.name, 
			      value: v.id,
			      ...v 
			    };
			  });
			  console.log(this.result.data,'处方类型数据');
			},
			// 判断是否为药理类别字段
			isPhamTypeField(field) {
				return field.inputBoxType === 'cascader' &&
					field.fieldName && field.fieldName.includes('药理类别');
			},

			// 获取选项的唯一key
			getOptionKey(option) {
				return option.id || option.value || Math.random().toString(36).substr(2, 9);
			},

			// 字段输入处理
			handleFieldInput(field) {
				this.updateForm();
			},

			// 字段变化处理
			handleFieldChange(field) {
				if (field.fieldCode === 'antcLv') {
					this.formDisabledAntcLv = !this.form.antcB;
				}

				if (field.inputBoxType === 'cascader' && !this.isPhamTypeField(field)) {
					this.handleCascaderChange(field, this.form[field.fieldCode]);
				}

				this.updateForm();
			},

			// 处理级联选择器变化
			handleCascaderChange(field, value) {
				//console.log(`级联选择器 ${field.fieldCode} 值变化:`, value);
			},

			// 获取字段占位符
			getFieldPlaceholder(field) {
				if (this.isPhamTypeField(field)) {
					return '请选择药理类别';
				}

				const placeholderMap = {
					'input': `请输入${field.fieldName}`,
					'selected': `请选择${field.fieldName}`,
					'textarea': `请输入${field.fieldName}`,
					'cascader': `请选择${field.fieldName}`,
					'checked': ''
				};

				return field.placeholder || placeholderMap[field.inputBoxType] || `请输入${field.fieldName}`;
			},

			// 初始化药理类别级联选择器
			initPhamTypeCascader() {
				//console.log('初始化药理类别级联选择器');
				this.loadFirstLevelPhamTypes();

				if (this.mode === 'edit' && (this.form.phamType1 || this.form.phamType2 || this.form.phamType3)) {
					this.initPhamTypeValue();
				}
			},

			// ========== 药理类别相关方法 ==========
			async loadFirstLevelPhamTypes() {
				try {
					this.loadingPhamTypes = true;
					const res = await selectOneCateGory({});

					if (res.code === 200) {
						this.phamTypeOptions = res.data.map(item => ({
							id: item.id,
							label: item.name || item.label,
							value: item.id,
							leaf: false,
							level: 1,
							idcode: item.idcode || item.id
						}));

						this.phamTypeOptions.forEach(item => {
							this.phamTypeCache.set(`1_${item.id}`, item);
						});

						//console.log('一级药理类别加载完成:', this.phamTypeOptions);
					}
				} catch (error) {
					//console.error('加载一级药理类别出错:', error);
					this.$message.error('加载药理类别失败');
				} finally {
					this.loadingPhamTypes = false;
				}
			},

			async loadPhamTypeChildren(node, resolve) {
				const {
					level,
					data
				} = node;

				try {
					let res;
					let children = [];

					if (level === 0) {
						resolve(this.phamTypeOptions);
						return;
					} else if (level === 1) {
						res = await selectTwoCateGory({
							idcode: data.idcode || data.id
						});
					} else if (level === 2) {
						res = await selectThreeCateGory({
							idcode: data.idcode || data.id
						});
					} else {
						resolve([]);
						return;
					}

					if (res.code === 200) {
						children = res.data.map(item => ({
							id: item.id,
							label: item.name || item.label,
							value: item.id,
							leaf: level === 2,
							level: level + 1,
							idcode: item.idcode || item.id
						}));

						children.forEach(child => {
							this.phamTypeCache.set(`${level + 1}_${child.id}`, child);
						});
					}

					resolve(children);
				} catch (error) {
					//console.error('加载药理子类别出错:', error);
					this.$message.error('加载子类别失败');
					resolve([]);
				}
			},

			handlePhamTypeChange(value) {
				if (!value || value.length === 0) {
					this.form.phamType1 = '';
					this.form.phamType2 = '';
					this.form.phamType3 = '';
					this.updateForm();
					return;
				}

				if (value.length >= 1) {
					this.form.phamType1 = value[0];
				} else {
					this.form.phamType1 = '';
				}

				if (value.length >= 2) {
					this.form.phamType2 = value[1];
				} else {
					this.form.phamType2 = '';
				}

				if (value.length >= 3) {
					this.form.phamType3 = value[2];
				} else {
					this.form.phamType3 = '';
				}

				// console.log('药理类别选择变化:', {
				// 	value: value,
				// 	phamType1: this.form.phamType1,
				// 	phamType2: this.form.phamType2,
				// 	phamType3: this.form.phamType3
				// });

				this.updateForm();
			},

			async initPhamTypeValue() {
				if (!this.form.phamType1) {
					this.phamTypeValue = [];
					return;
				}

				try {
					const path = [];

					if (this.form.phamType1) {
						const level1 = await this.loadPhamTypeById(this.form.phamType1, 1);
						if (level1) {
							path.push(level1.id);

							if (this.form.phamType2) {
								const level2 = await this.loadPhamTypeById(this.form.phamType2, 2, level1.id);
								if (level2) {
									path.push(level2.id);

									if (this.form.phamType3) {
										const level3 = await this.loadPhamTypeById(this.form.phamType3, 3, level2.id);
										if (level3) {
											path.push(level3.id);
										}
									}
								}
							}
						}
					}

					this.phamTypeValue = path;
					//console.log('初始化药理类别值:', path);
				} catch (error) {
					//console.error('初始化药理类别失败:', error);
					this.phamTypeValue = [];
				}
			},

			async loadPhamTypeById(id, level, parentId = null) {
				const cacheKey = `${level}_${id}`;

				if (this.phamTypeCache.has(cacheKey)) {
					return this.phamTypeCache.get(cacheKey);
				}

				try {
					let res;
					let foundItem = null;

					if (level === 1) {
						if (this.phamTypeOptions.length === 0) {
							await this.loadFirstLevelPhamTypes();
						}

						foundItem = this.phamTypeOptions.find(item => item.id == id);
					} else if (level === 2) {
						res = await selectTwoCateGory({
							idcode: parentId
						});
						if (res.code === 200) {
							foundItem = res.data.find(item => item.id == id);
							if (foundItem) {
								foundItem = {
									id: foundItem.id,
									label: foundItem.name || foundItem.label,
									value: foundItem.id,
									leaf: false,
									level: 2,
									idcode: foundItem.idcode || foundItem.id
								};
							}
						}
					} else if (level === 3) {
						res = await selectThreeCateGory({
							idcode: parentId
						});
						if (res.code === 200) {
							foundItem = res.data.find(item => item.id == id);
							if (foundItem) {
								foundItem = {
									id: foundItem.id,
									label: foundItem.name || foundItem.label,
									value: foundItem.id,
									leaf: true,
									level: 3,
									idcode: foundItem.idcode || foundItem.id
								};
							}
						}
					}

					if (foundItem) {
						this.phamTypeCache.set(cacheKey, foundItem);
						return foundItem;
					}
				} catch (error) {
					//console.error(`加载${level}级药理类别失败:`, error);
				}

				return null;
			},

			handleCascaderVisible(visible) {
				if (visible && this.phamTypeOptions.length === 0) {
					this.loadFirstLevelPhamTypes();
				}
			},

			getPhamTypeRules() {
				const phamTypeFields = this.phamTypeFields;
				const requiredFields = phamTypeFields.filter(field => field.isMustFill === 1 || field.isMustChoose === 1);

				if (requiredFields.length > 0) {
					return [{
						required: true,
						message: '请选择药理类别',
						trigger: 'change'
					}];
				}

				return [];
			},

			// ========== 原有方法保持不变 ==========
			async loadUnitOptions() {
				try {
					const pacUntRes = await getDrugUnt();
					if (pacUntRes.code === 200) {
						this.allUnitOptions = pacUntRes.data.map(v => ({
							label: v.name,
							...v
						}));
					}

					const dosUntRes = await getDosaUnt();
					if (dosUntRes.code === 200) {
						this.dosUnt = dosUntRes.data.map(v => ({
							label: v.name,
							...v
						}));
						this.minUnt = dosUntRes.data.map(v => ({
							label: v.name,
							...v
						}));
					}
				} catch (error) {
					//console.error('加载单位选项失败:', error);
				}
			},

			async loadMedicationOptions() {
				try {
					const frequencyRes = await getMedicationWay();
					// //console.log(frequencyRes,'frequencyResfrequencyRes');
					if (frequencyRes.code === 200) {
						this.usedFrquIdOptions = frequencyRes.data.map(v => ({
							label: v.name,
							...v
						}));
					}

					const wayRes = await getMedicationFrequency();
					if (wayRes.code === 200) {
						this.medcWayIdOptions = wayRes.data.map(v => ({
							label: v.name,
							...v
						}));
					}

					const medcasRes = await getMedcas();
					if (medcasRes.code === 200) {
						this.medicalRecordFeeOption = medcasRes.data.map(v => ({
							label: v.name,
							id: v.id,
							...v
						}));
					}
				} catch (error) {
					//console.error('加载用药选项失败:', error);
				}
			},

			async loadOtherOptions() {
				try {
					const docVtRes = await selectDocVtByType();
					if (docVtRes.code === 200) {
						this.poisonousTypeOption = docVtRes.data.map(v => ({
							label: v.name,
							id: v.id,
							...v
						}));
					}
				} catch (error) {
					//console.error('加载其他选项失败:', error);
				}
			},

			setFieldConfig(fieldConfig) {
				if (fieldConfig && fieldConfig.length > 0) {
					// //console.log('F3组件接收到父组件传递的字段配置:', fieldConfig);

					if (this.fieldConfigProcessed) {
						this.processFieldConfigImmediately(fieldConfig);
					} else {
						this.pendingFieldConfig = fieldConfig;
						// //console.log('字段配置已保存，等待初始化完成后处理');
					}
				} else {
					//console.warn('F3组件：未接收到有效的字段配置');
					this.setupDefaultFields();
				}
			},

			processPendingFieldConfig() {
				if (this.pendingFieldConfig) {
					this.processFieldConfigImmediately(this.pendingFieldConfig);
					this.pendingFieldConfig = null;
				}
			},

			processFieldConfigImmediately(fieldConfig) {
				try {
					this.processFieldData(fieldConfig);
					this.fieldConfigProcessed = true;
					// this.$message.success('F3组件字段配置加载成功');
				} catch (error) {
					// //console.error('处理字段配置失败:', error);
					this.$message.error('字段配置处理失败');
					this.setupDefaultFields();
				}
			},

			processFieldData(fieldData) {
				try {
					const visibleFields = fieldData.filter(field =>
						field.isShow === 1 || field.isMustFill === 1 || field.isMustChoose === 1
					);

					this.dynamicFields = visibleFields.map(field => ({
						fieldCode: field.fieldCode,
						fieldName: field.fieldName || this.getDefaultFieldName(field.fieldCode),
						inputBoxType: field.inputBoxType || 'input',
						isShow: true,
						isMustFill: field.isMustFill || 0,
						isMustChoose: field.isMustChoose || 0,
						placeholder: field.placeholder || '',
						options: field.options || [],
						rules: field.rules || [],
						colSpan: field.colSpan || 6,
						fieldCodeOption: field.fieldCodeOption || field.fieldCode,
						cascaderProps: field.cascaderProps || this.getDefaultCascaderProps(field.fieldCode),
						cascaderOptions: field.cascaderOptions || [],
					}));

					this.buildFieldOptionsMap();
					this.buildFieldRulesMap();
					this.buildFieldCodeOptionsMap();
					this.initCascaderOptions();

					// //console.log('F3组件处理后的动态字段:', this.dynamicFields);
					this.fieldConfigProcessed = true;
					this.$forceUpdate();
				} catch (error) {
					//console.error('处理字段数据失败:', error);
					throw error;
				}
			},

			initCascaderOptions() {
				this.dynamicFields.forEach(field => {
					if (field.inputBoxType === 'cascader' && !this.isPhamTypeField(field)) {
						if (field.cascaderOptions && field.cascaderOptions.length > 0) {
							this.cascaderOptionsMap.set(field.fieldCode, field.cascaderOptions);
						} else {
							this.loadDefaultCascaderOptions(field);
						}
					}
				});
			},

			loadDefaultCascaderOptions(field) {
				const defaultOptions = this.getDefaultCascaderOptions(field.fieldCode);
				if (defaultOptions && defaultOptions.length > 0) {
					this.cascaderOptionsMap.set(field.fieldCode, defaultOptions);
				}
			},

			getDefaultCascaderProps(fieldCode) {
				const propsMap = {
					default: {
						value: 'id',
						label: 'label',
						children: 'children',
						checkStrictly: true,
						emitPath: true,
						expandTrigger: 'hover'
					}
				};

				return propsMap[fieldCode] || propsMap.default;
			},

			buildFieldOptionsMap() {
				this.fieldOptionsMap.clear();
				this.dynamicFields.forEach(field => {
					if (field.options && field.options.length > 0) {
						this.fieldOptionsMap.set(field.fieldCode, field.options);
					}
				});
			},

			buildFieldRulesMap() {
				this.fieldRulesMap.clear();
				this.dynamicFields.forEach(field => {
					if (field.rules && field.rules.length > 0) {
						this.fieldRulesMap.set(field.fieldCode, field.rules);
					}
				});
				//console.log('F3字段验证规则映射构建完成:', this.fieldRulesMap);
			},

			buildFieldCodeOptionsMap() {
				this.fieldCodeOptionsMap.clear();
				this.dynamicFields.forEach(field => {
					if (field.inputBoxType === 'selected') {
						const optionsArray = this.getOptionsByFieldCodeOption(field.fieldCodeOption);
						if (optionsArray && optionsArray.length > 0) {
							this.fieldCodeOptionsMap.set(field.fieldCode, optionsArray);
						}
					}
				});
				//console.log('F3 fieldCode选项映射构建完成:', this.fieldCodeOptionsMap);
			},

			rebuildFieldMaps() {
				this.buildFieldOptionsMap();
				this.buildFieldRulesMap();
				this.buildFieldCodeOptionsMap();
			},

			getOptionsByFieldCodeOption(fieldCodeOption) {
				const optionsMap = {
					'basMednB': this.optionDataMap.YNoptions,
					'statusOptions': this.optionDataMap.statusOptions,
					'highRiskOption': this.optionDataMap.highRiskOption,
					'saveOptions': this.optionDataMap.saveOptions,
					'roundUnt': this.roundUntoptions,
					'antcB': this.optionDataMap.antcB,
					'chargedSpcOptions': this.optionDataMap.chargedSpcOptions,
					'UseOptions': this.optionDataMap.UseOptions,
					'nurtureB': this.optionDataMap.nurtureB,
					'nurtureType': this.optionDataMap.nurtureType,
					'expensiveType': this.optionDataMap.expensiveTypeOptions,
					'liquidOptions': this.optionDataMap.liquidOptions,
					'lmtSpc': this.optionDataMap.UseOptions,
					'qltTypeOptions': this.optionDataMap.qltTypeOptions,
					'minUnt': this.minUnt,
					'dosUnt': this.dosUnt,
					'pacUnt': this.allUnitOptions,
					'usedFrquId': this.usedFrquIdOptions,
					'medcWayId': this.medcWayIdOptions,
					'mrsId': this.medicalRecordFeeOption,
					'intxAnstType': this.poisonousTypeOption,
				};

				const optionsArray = optionsMap[fieldCodeOption];
				//console.log(`F3查找fieldCodeOption: ${fieldCodeOption}, 找到选项数组:`, optionsArray);

				return optionsArray || [];
			},

			getFieldOptions(field) {
				if (this.fieldCodeOptionsMap.has(field.fieldCode)) {
					const options = this.fieldCodeOptionsMap.get(field.fieldCode);
					//console.log(`F3从fieldCodeOptionsMap获取字段 ${field.fieldCode} 的选项:`, options);
					return options;
				}

				if (field.options && field.options.length > 0) {
					//console.log(`F3使用字段自带的options获取字段 ${field.fieldCode} 的选项:`, field.options);
					return field.options;
				}

				const defaultOptions = this.getDefaultFieldOptions(field.fieldCode);
				return defaultOptions;
			},

			getCascaderOptions(field) {
				if (field.inputBoxType === 'cascader' && !this.isPhamTypeField(field)) {
					return this.cascaderOptionsMap.get(field.fieldCode) || [];
				}
				return [];
			},

			getCascaderProps(field) {
				if (field.inputBoxType === 'cascader' && !this.isPhamTypeField(field)) {
					return field.cascaderProps || this.getDefaultCascaderProps(field.fieldCode);
				}
				return {};
			},
			// 清除表单验证
			clearFormValidation() {
				if (this.$refs.form) {
					this.$refs.form.clearValidate();
				}
			},
			getDefaultFieldName(fieldCode) {
				const fieldNameMap = {
					'highRisk': '高危药品',
					'liquid': '是否液体',
					'qltType': '质量类别',
					'hormone': '激素药品',
					'prey': '妊娠药品',
					'hospPrep': '院内制剂',
					'retTur': '抗肿瘤',
					'mainCtrl': '重点监控',
					'prnAssePrep': '蛋白同化剂',
					'prodAgatExpo': '避光药物',
					'lmtUsed': '限定用药',
					'basMedn': '基本药物',
					'basMednType': '基药类型',
					'antcLv': '抗生素等级',
					'importB': '是否进口',
					'chargedSpc': '计费范围',
					'stoeCons': '存储条件',
					'allowDecimal': '允许小数',
					'selfProe': '自备药品',
					'dddvar': 'DDD',
					'fromClass': '来源分类',
					'strhCoet': '强度系数',
					'stopDay': '停药天数',
					'weit': '权重',
					'soltquayCnt': '溶媒量',
					'stoeConds': '储存条件',
					'matl': '材质',
					'cutOff': '检验测试',
					'reatCode': '试剂条码',
					'convnRelp': '换算关系',
					'compareAvg': '对照均值',
					'lmtUsedRemark': '限定备注',
					'phamType1': '药理类别I',
					'phamType2': '药理类别II',
					'phamType3': '药理类别III',
					'phamType': '药理类别'
				};

				return fieldNameMap[fieldCode] || fieldCode;
			},

			getDefaultFieldOptions(fieldCode) {
				const optionsMap = {
					'highRisk': this.optionDataMap.highRiskOption,
					'liquid': this.optionDataMap.liquidOptions,
					'qltTypeOptions': this.optionDataMap.qltTypeOptions,
					'hormone': this.optionDataMap.YNoptions,
					'prey': this.optionDataMap.YNoptions,
					'hospPrep': this.optionDataMap.YNoptions,
					'retTur': this.optionDataMap.YNoptions,
					'mainCtrl': this.optionDataMap.YNoptions,
					'prnAssePrep': this.optionDataMap.YNoptions,
					'rxType': this.rxTypeoptions,
					'expensiveType': this.optionDataMap.expensiveTypeOptions,
					'prodAgatExpo': this.optionDataMap.YNoptions,
					'lmtUsed': this.optionDataMap.YNoptions,
					'basMedn': this.optionDataMap.YNoptions,
					'antcLv': this.optionDataMap.antcB,
					'importB': this.optionDataMap.YNoptions,
					'chargedSpc': this.optionDataMap.chargedSpcOptions,
					'stoeCons': this.optionDataMap.saveOptions,
				};

				return optionsMap[fieldCode] || [];
			},

			getFieldRules(field) {
				if (this.isPhamTypeField(field)) {
					return this.getPhamTypeRules();
				}

				const baseRules = this.fieldRulesMap.get(field.fieldCode) || [];

				if (field.isMustFill === 1 || field.isMustChoose === 1) {
					const requiredRule = {
						required: true,
						message: `请输入${field.fieldName}`,
						trigger: 'blur'
					};

					if (field.inputBoxType === 'selected') {
						requiredRule.message = `请选择${field.fieldName}`;
						requiredRule.trigger = 'change';
					}

					if (field.inputBoxType === 'cascader') {
						requiredRule.message = `请选择${field.fieldName}`;
						requiredRule.trigger = 'change';
					}

					return [requiredRule, ...baseRules];
				}

				return baseRules;
			},

			setupDefaultFields() {
				const defaultFields = [{
						fieldCode: 'highRisk',
						fieldName: '高危药品',
						inputBoxType: 'selected',
						fieldCodeOption: 'highRiskOption',
						isShow: 1
					},
					{
						fieldCode: 'liquid',
						fieldName: '是否液体',
						inputBoxType: 'selected',
						fieldCodeOption: 'liquidOptions',
						isShow: 1
					},
					{
						fieldCode: 'qltType',
						fieldName: '质量类别',
						inputBoxType: 'selected',
						fieldCodeOption: 'qltTypeOptions',
						isShow: 1
					},
					{
						fieldCode: 'hormone',
						fieldName: '激素药品',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'prey',
						fieldName: '妊娠药品',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'hospPrep',
						fieldName: '院内制剂',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'retTur',
						fieldName: '抗肿瘤',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'mainCtrl',
						fieldName: '重点监控',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'prnAssePrep',
						fieldName: '蛋白同化剂',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'prodAgatExpo',
						fieldName: '避光药物',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'lmtUsed',
						fieldName: '限定用药',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'basMedn',
						fieldName: '基本药物',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'basMednType',
						fieldName: '基药类型',
						inputBoxType: 'selected',
						isShow: 1
					},
					{
						fieldCode: 'antcLv',
						fieldName: '抗生素等级',
						inputBoxType: 'selected',
						fieldCodeOption: 'antcB',
						isShow: 1
					},
					{
						fieldCode: 'importB',
						fieldName: '是否进口',
						inputBoxType: 'selected',
						fieldCodeOption: 'YNoptions',
						isShow: 1
					},
					{
						fieldCode: 'chargedSpc',
						fieldName: '计费范围',
						inputBoxType: 'selected',
						fieldCodeOption: 'chargedSpcOptions',
						isShow: 1
					},
					{
						fieldCode: 'roundUnt',
						fieldName: '取整方式',
						inputBoxType: 'selected',
						fieldCodeOption: 'roundUntoptions',
						isShow: 1
					},
					{
						fieldCode: 'stoeCons',
						fieldName: '存储条件',
						inputBoxType: 'selected',
						fieldCodeOption: 'saveOptions',
						isShow: 1
					},
					{
						fieldCode: 'allowDecimal',
						fieldName: '允许小数',
						inputBoxType: 'checked',
						isShow: 1
					},
					{
						fieldCode: 'selfProe',
						fieldName: '自备药品',
						inputBoxType: 'checked',
						isShow: 1
					},
					{
						fieldCode: 'dddvar',
						fieldName: 'DDD',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'fromClass',
						fieldName: '来源分类',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'strhCoet',
						fieldName: '强度系数',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'stopDay',
						fieldName: '停药天数',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'weit',
						fieldName: '权重',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'soltquayCnt',
						fieldName: '溶媒量',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'stoeConds',
						fieldName: '储存条件',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'matl',
						fieldName: '材质',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'cutOff',
						fieldName: '检验测试',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'reatCode',
						fieldName: '试剂条码',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'convnRelp',
						fieldName: '换算关系',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'compareAvg',
						fieldName: '对照均值',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'lmtUsedRemark',
						fieldName: '限定备注',
						inputBoxType: 'textarea',
						isShow: 1
					},
					{
						fieldCode: 'phamType1',
						fieldName: '药理类别I',
						inputBoxType: 'cascader',
						isShow: 1
					},
					{
						fieldCode: 'phamType2',
						fieldName: '药理类别II',
						inputBoxType: 'cascader',
						isShow: 1
					},
					{
						fieldCode: 'phamType3',
						fieldName: '药理类别III',
						inputBoxType: 'cascader',
						isShow: 1
					}
				];
				this.processFieldData(defaultFields);
			},

			updateForm() {
				this.$emit('update', this.form);
			},

			resetForm() {
				Object.keys(this.form).forEach(key => {
					if (typeof this.form[key] === 'boolean') {
						this.form[key] = false;
					} else if (typeof this.form[key] === 'number') {
						this.form[key] = 0;
					} else {
						this.form[key] = '';
					}
				});

				this.phamTypeValue = [];
				this.form.phamType1 = '';
				this.form.phamType2 = '';
				this.form.phamType3 = '';

				this.form.highRisk = 0;
				this.form.liquid = 0;
				this.form.basMedn = 0;
				this.form.antcLv = 0;
				this.form.state = 1;

				if (this.$refs.form) {
					this.$refs.form.clearValidate();
				}
			},

			setAntcStatus(isAntc) {
				this.formDisabledAntcLv = !isAntc;
				this.updateForm();
			}
		}
	};
</script>

<style lang="less" scoped>
.basic-info {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .el-form {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    
    .form-scroll-container {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 12px;
      box-sizing: border-box;
      
      /* 滚动条样式 */
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 4px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
        
        &:hover {
          background: #a8a8a8;
        }
      }
      
      /* 针对Firefox的滚动条样式 */
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f5f5f5;
      
      .dynamic-fields-grid {
        .field-col {
          margin-bottom: 12px;
          
          .form-item-adaptive {
            margin-bottom: 8px;
            display: flex;
            align-items: stretch;
            height: 100%;
            
            ::v-deep .el-form-item__label {
              background-color: #f5f7fa;
              border: 1px solid #dcdfe6;
              border-right: none;
              padding: 0 8px;
              line-height: 32px;
              height: 100%;
              font-weight: bold;
              color: #00557f;
              width: auto;
              min-width: 100px;
              max-width: 150px;
              text-align: left;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              font-size: 12px;
              border-top-left-radius: 4px;
              border-bottom-left-radius: 4px;
              white-space: normal;
              word-break: break-word;
              overflow-wrap: break-word;
              flex-shrink: 0;
            }
            
            ::v-deep .el-form-item__content {
              border: 1px solid #dcdfe6;
              border-left: none;
              line-height: 32px;
              height: 100%;
              flex: 1;
              margin-left: 0 !important;
              display: flex;
              align-items: center;
              border-top-right-radius: 4px;
              border-bottom-right-radius: 4px;
              
              .compact-input,
              .compact-select,
              .compact-cascader,
              .compact-textarea {
                width: 100%;
                height: 100%;
                
                .el-input__inner,
                .el-textarea__inner {
                  border: none;
                  height: 100%;
                  line-height: 30px;
                  padding: 0 8px;
                  font-size: 12px;
                  color: #606266;
                  text-align: left;
                }
              }
              
              /* 复选框样式 */
              .compact-checkbox {
                font-size: 12px;
                color: #606266;
                text-align: left;
                
                ::v-deep .el-checkbox__input {
                  margin-right: 4px;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>